import { useEffect, useMemo, useRef, useState } from 'react';
import { getFeaturedAlbums } from '../services/api';
import { Album } from '../types';
import { useMusicState } from './storeHooks/useMusicState';

interface UseFeaturedAlbumsReturn {
  music: Album[];
  serenity: Album[];
  books: Album[];
  indianMusic: Album[];
  musicGenres: string[];
  serenityGenres: string[];
  booksGenres: string[];
  indianMusicGenres: string[];
  loading: boolean;
  error: string | null;
  filterByGenre: (
    category: 'music' | 'serenity' | 'books' | 'indian-music',
    genre: string,
  ) => Album[];
}

/**
 * Custom hook for fetching featured albums on home page
 * Calls single API endpoint /api/featured/albums
 * Returns all items grouped by category (no pagination)
 * Provides frontend filtering by genre without API calls
 * Only used on home page
 */
export const useFeaturedAlbums = (): UseFeaturedAlbumsReturn => {
  const {
    featuredMusic,
    featuredSerenity,
    featuredBooks,
    setFeaturedMusic,
    setFeaturedSerenity,
    setFeaturedBooks,
  } = useMusicState();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [indianMusic, setIndianMusic] = useState<Album[]>([]);
  const hasInitiatedFetch = useRef(false);

  // Use Redux state for featured music, serenity, and books (separate from paginated data)
  const music = featuredMusic;
  const serenity = featuredSerenity;
  const books = featuredBooks;

  // Extract unique genres from each category
  const musicGenres = useMemo(() => {
    const genres = new Set<string>();
    music.forEach((album) => {
      album.genre?.forEach((g) => genres.add(g));
    });
    return Array.from(genres).sort();
  }, [music]);

  const serenityGenres = useMemo(() => {
    const genres = new Set<string>();
    serenity.forEach((album) => {
      album.genre?.forEach((g) => genres.add(g));
    });
    return Array.from(genres).sort();
  }, [serenity]);

  const booksGenres = useMemo(() => {
    const genres = new Set<string>();
    books.forEach((album) => {
      album.genre?.forEach((g) => genres.add(g));
    });
    return Array.from(genres).sort();
  }, [books]);

  const indianMusicGenres = useMemo(() => {
    const genres = new Set<string>();
    indianMusic.forEach((album) => {
      album.genre?.forEach((g) => genres.add(g));
    });
    return Array.from(genres).sort();
  }, [indianMusic]);

  // Filter albums by genre (frontend filtering, no API call)
  const filterByGenre = (
    category: 'music' | 'serenity' | 'books' | 'indian-music',
    genre: string,
  ): Album[] => {
    const data =
      category === 'music'
        ? music
        : category === 'serenity'
        ? serenity
        : category === 'books'
        ? books
        : indianMusic;

    if (genre === 'All') {
      return data;
    }

    return data.filter((album) => album.genre?.includes(genre));
  };

  useEffect(() => {
    const fetchFeaturedAlbums = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getFeaturedAlbums();

        // Set data to Redux store (featured data, separate from paginated data)
        setFeaturedMusic(response.music?.items || []);
        setFeaturedSerenity(response.serenity?.items || []);
        setFeaturedBooks(response.books?.items || []);
        setIndianMusic(
          response['indian-music']?.items || response.indianMusic?.items || [],
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load featured albums';
        setError(errorMessage);
        console.error('Error fetching featured albums:', err);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if Redux store is empty and added check if initiated
    if (
      music.length === 0 &&
      serenity.length === 0 &&
      books.length === 0 &&
      indianMusic.length === 0 &&
      !hasInitiatedFetch.current
    ) {
      hasInitiatedFetch.current = true;
      fetchFeaturedAlbums();
    }
  }, [music.length, serenity.length, books.length, indianMusic.length]);

  return {
    music,
    serenity,
    books,
    indianMusic,
    musicGenres,
    serenityGenres,
    booksGenres,
    indianMusicGenres,
    loading,
    error,
    filterByGenre,
  };
};

export default useFeaturedAlbums;
